import { getClientHost, getDomainKey } from './getDomainInfo';
import { UTMParams, stampUTMParams } from './utmParams';

const PLATFORM_HOST = 'platform.2gis.ru';

/** Сервисы, куда пробрасываются UTM-метки; в документацию метки не передаём */
const UTM_HOSTS = [
  PLATFORM_HOST,
  'pro.2gis.ru',
  'geoflow.2gis.ru',
  'data.2gis.ru',
  'data.2gis.com',
];

/**
 * Проверяет, нужна ли локализация (добавление /en)
 */
function needsLocalization(): boolean {
  return getDomainKey(getClientHost()) === 'com';
}

/**
 * Ставит в ссылки на UTM_HOSTS метки со штампом продукта (см. stampUTMParams).
 * Ссылкам Platform Manager на не-RU доменах добавляет /en.
 */
export const applyUtmParamsToHref = (
  href: string,
  utmParams: UTMParams,
  product: string,
): string => {
  if (!href || !UTM_HOSTS.some((host) => href.includes(host))) {
    return href;
  }

  try {
    const url = new URL(href);
    const isPlatform = url.hostname === PLATFORM_HOST;

    if (!UTM_HOSTS.includes(url.hostname)) {
      return href;
    }

    // Применяем локализацию
    if (isPlatform && needsLocalization() && !url.pathname.startsWith('/en')) {
      url.pathname = `/en${url.pathname}`;
    }

    Object.entries(stampUTMParams(utmParams, product)).forEach(
      ([key, value]) => {
        if (key.startsWith('utm_') && value) {
          url.searchParams.set(key, value);
        }
      },
    );

    return url.toString();
  } catch {
    return href;
  }
};

/** Применяет applyUtmParamsToHref ко всем подходящим ссылкам в HTML */
export const applyUtmParamsToHtml = (
  html: string,
  utmParams: UTMParams,
  product: string,
): string => {
  if (!html || typeof window === 'undefined') {
    return html;
  }

  if (!UTM_HOSTS.some((host) => html.includes(host))) {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll<HTMLAnchorElement>(
      UTM_HOSTS.map((host) => `a[href*="${host}"]`).join(','),
    );

    links.forEach((link) => {
      const currentHref = link.getAttribute('href') || '';
      const updatedHref = applyUtmParamsToHref(currentHref, utmParams, product);

      if (updatedHref !== currentHref) {
        link.setAttribute('href', updatedHref);
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};
